import { Group } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colorToCss, GROUP_COLORS, sameColor, toGroupColor } from "../utils/groupColor";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useAddOrUpdateGroup, useRemoveGroup } from "@/hooks/groups/useAddOrUpdateGroup";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";

type Props = {
  selectedGroup: Group;
  parentId: string | null;
  parentName: string;
  canModify: boolean;
  isMobile: boolean;
  onChildAdded: (newGroupId: string) => void;
  onDeleted: () => void;
};

export default function GroupEditPanel({
  selectedGroup,
  parentId,
  parentName,
  canModify,
  isMobile,
  onChildAdded,
  onDeleted,
}: Props) {
  const { t } = useTranslation();
  const { geotabClient } = useGeotabClient();
  const addOrUpdateGroup = useAddOrUpdateGroup();
  const removeGroup = useRemoveGroup();

  const [nameValue, setNameValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [colorValue, setColorValue] = useState(() => toGroupColor(selectedGroup));
  const [newChildName, setNewChildName] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setNameValue(selectedGroup.name ?? "");
    setDescriptionValue(selectedGroup.comments ?? "");
    setColorValue(toGroupColor(selectedGroup));
  }, [selectedGroup]);

  // A result belongs to the group it was produced for, so it goes when another group is selected.
  // Keyed on the id: a refetch after saving replaces the object but must not clear the message.
  // Adding a child selects it and deleting one selects its parent; those messages are meant to be
  // seen there.
  const keepAlertForId = useRef<string | null>(null);
  useEffect(() => {
    if (keepAlertForId.current === selectedGroup.id) {
      keepAlertForId.current = null;
      return;
    }
    setActionError(null);
    setActionSuccess(null);
  }, [selectedGroup.id]);

  const hasEditChanges =
    nameValue.trim() !== (selectedGroup.name ?? "") ||
    descriptionValue.trim() !== (selectedGroup.comments ?? "") ||
    !sameColor(colorValue, toGroupColor(selectedGroup));
  const isSaving = addOrUpdateGroup.isPending;

  const succeed = (message: string) => {
    setActionError(null);
    setActionSuccess(message);
  };
  /** The translated summary followed by Geotab's own reason, e.g. why a Remove was refused. */
  const fail = (message: string, error: unknown) => {
    setActionSuccess(null);
    setActionError(`${message} ${getErrorMessage(error)}`);
  };

  const save = () => {
    if (!parentId) return;
    addOrUpdateGroup.mutate(
      {
        geotabClient,
        method: "Set",
        groupId: selectedGroup.id,
        name: nameValue.trim(),
        comment: descriptionValue.trim(),
        color: colorValue,
        parentId,
      },
      {
        onSuccess: () => succeed(t("groupsPage.saveSuccess")),
        onError: error => fail(t("groupsPage.saveError"), error),
      },
    );
  };

  const addChild = () => {
    addOrUpdateGroup.mutate(
      {
        geotabClient,
        method: "Add",
        name: newChildName.trim(),
        comment: "",
        color: toGroupColor(selectedGroup),
        parentId: selectedGroup.id,
      },
      {
        onSuccess: newId => {
          succeed(t("groupsPage.addSuccess"));
          setNewChildName("");
          if (typeof newId === "string") {
            keepAlertForId.current = newId;
            onChildAdded(newId);
          }
        },
        onError: error => fail(t("groupsPage.addError"), error),
      },
    );
  };

  const deleteGroup = () => {
    if (!parentId) return;
    removeGroup.mutate(
      { geotabClient, groupId: selectedGroup.id, parentId },
      {
        onSuccess: () => {
          succeed(t("groupsPage.deleteSuccess"));
          setShowDeleteConfirm(false);
          // Deleting selects the parent; the message is meant to be seen there.
          keepAlertForId.current = parentId;
          onDeleted();
        },
        onError: error => {
          fail(t("groupsPage.deleteError"), error);
          setShowDeleteConfirm(false);
        },
      },
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: "#f8f9fb",
        // border-box so the padding and border stay inside the 25% basis; the tree takes the rest.
        boxSizing: "border-box",
        flex: isMobile ? "1 1 auto" : "0 0 25%",
        minWidth: isMobile ? 0 : 300,
      }}
    >
      <Stack spacing={1.25} sx={{ width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t("groupsPage.groupActions")}
        </Typography>

        <TextField
          size="small"
          label={t("groupsPage.parentLabel")}
          value={parentName}
          disabled
          fullWidth
        />

        <Stack spacing={1.25} sx={{ width: "100%" }}>
          <TextField
            size="small"
            label={t("groupsPage.nameLabel")}
            value={nameValue}
            onChange={event => setNameValue(event.target.value)}
            disabled={!canModify || isSaving}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={!canModify || nameValue.trim().length === 0 || !hasEditChanges || isSaving}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("groupsPage.saveAction")}
          </Button>
        </Stack>

        <TextField
          size="small"
          multiline
          minRows={3}
          label={t("groupsPage.descriptionLabel")}
          value={descriptionValue}
          onChange={event => setDescriptionValue(event.target.value)}
          disabled={!canModify || isSaving}
          fullWidth
          sx={{
            "& .MuiInputBase-inputMultiline": {
              resize: "vertical",
            },
          }}
        />

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t("groupsPage.colorLabel")}
          </Typography>
          <Box
            sx={{
              width: 180,
              height: 28,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: colorToCss(colorValue),
            }}
          />
          <Stack direction="row" spacing={1}>
            {GROUP_COLORS.map(color => (
              <Box
                key={`${color.r}-${color.g}-${color.b}-${color.a}`}
                component="button"
                type="button"
                onClick={() => setColorValue(color)}
                disabled={!canModify || isSaving}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 0.5,
                  border: "2px solid",
                  borderColor: sameColor(colorValue, color) ? "text.primary" : "divider",
                  backgroundColor: colorToCss(color),
                  cursor: canModify && !isSaving ? "pointer" : "default",
                  p: 0,
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Stack spacing={1.25} sx={{ width: "100%" }}>
          <TextField
            size="small"
            label={t("groupsPage.addLabel")}
            value={newChildName}
            onChange={event => setNewChildName(event.target.value)}
            disabled={isSaving}
            fullWidth
          />
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={addChild}
              disabled={newChildName.trim().length === 0 || isSaving}
            >
              {t("groupsPage.addAction")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!canModify || removeGroup.isPending}
            >
              {t("groupsPage.deleteAction")}
            </Button>
          </Stack>
        </Stack>

        {!canModify ? (
          <Typography variant="caption" color="text.secondary">
            {t("groupsPage.protectedHint")}
          </Typography>
        ) : null}

        {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}
        {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      </Stack>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>{t("groupsPage.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("groupsPage.deleteConfirmBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>{t("groupsPage.cancelAction")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteGroup}
            disabled={removeGroup.isPending}
          >
            {t("groupsPage.deleteConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
